import { createSlice } from "@reduxjs/toolkit";
import {
    setDisabledModules,
    updateDisabledFields,
} from "../exceptions/disabledFieldsSlice";
import {
    generateCatalogNumber,
    manageConfiguratorState,
    setErrorFields,
    setErrors,
    setReadyForShapeSummary, setShapeElementsMatchSelections, setShapeRequestPending
} from "../configuratorStateSlice";
import {setAttributeSelections, setQuoteItemModuleAttributeSelections} from "./productDataSlice";
import {getSummary} from "../summarySlice";
import {addElement, clearElements, elementParentUpdatedSelection, mapSectionsToSide} from "./productElementsSlice";
import {useSelector} from "react-redux";


export const productSelectionsSlice =  createSlice({
    name: 'productSelections',
    initialState: {
        selectionsLoading: 'idle',
        requiredAttributes: [],
        optionalAttributes: [],
        perModuleAttributes: [],
        shapedAttributes: [],
        requiredSelections: [],
        optionalSelections: [],
        perModuleSelections: [],
        shapedSelections: [],
    },
    reducers: {
        productSelectionsLoading(state ,action){
            if(state.selectionsLoading == 'idle'){
                state.selectionsLoading = 'pending'
            }
        },
        productAttributesSet(state, action) {
            if(state.selectionsLoading == 'pending'){
                state.selectionsLoading = 'idle';
                state.requiredAttributes = action.payload.requiredAttributes
                state.optionalAttributes = action.payload.optionalAttributes
                state.perModuleAttributes = action.payload.perModuleAttributes
                state.shapedAttributes = action.payload.shapedAttributes
            }
        },
        sideAttributesSet(state, action) {
            if(state.selectionsLoading == 'pending'){
                state.selectionsLoading = 'idle';
                state.sideAttributes = action.payload;
            }
        },
        sideOptionalAttributesSet(state, action) {
            if(state.selectionsLoading == 'pending'){
                state.selectionsLoading = 'idle';
                state.sideOptionalAttributes = action.payload;
            }
        },
        requiredSelectionsSet(state, action){
            if( state.selectionsLoading == 'pending'){
                state.selectionsLoading = 'idle';
                state.requiredSelections = action.payload;
            }
        },
        optionalSelectionsSet(state, action){
            if( state.selectionsLoading == 'pending'){
                state.selectionsLoading = 'idle';
                state.optionalSelections = action.payload;
            }
        },
        shapedSelectionsSet(state, action) {
            if(state.selectionsLoading == 'pending'){
                state.selectionsLoading = 'idle';
                state.shapedSelections = action.payload;
            }
        },
        perModuleSelectionsSet(state, action){
            if( state.selectionsLoading == 'pending'){
                state.selectionsLoading = 'idle';
                state.perModuleSelections = action.payload;
            }
        },
        sideSelectionsSet(state, action) {
            if(state.selectionsLoading == 'pending'){
                state.selectionsLoading = 'idle';
                state.sideSelections = action.payload;
            }
        },
        sideOptionalSelectionsSet(state, action) {
            if(state.selectionsLoading == 'pending'){
                state.selectionsLoading = 'idle';
                state.sideOptionalSelections = action.payload;
            }
        },
        selectionsSelectionsClear(state, action){
            state.requiredSelections = []
            state.optionalSelections = []
            state.perModuleSelections = []
            state.sideSelections = []
        }
    }
});
export const { productSelectionsLoading, shapedSelectionsSet, productAttributesSet, requiredSelectionsSet, optionalSelectionsSet, perModuleSelectionsSet, selectionsSelectionsClear } = productSelectionsSlice.actions;

export const separateProductAttributes = (productAttributes) => (dispatch, getState) => {
    dispatch(productSelectionsLoading());

    const shaped = productAttributes.filter( pa => pa.part_of_geometry)
    const required = productAttributes.filter(pa => !pa.optional && !pa.per_module && !pa.read_only && !pa.part_of_geometry);
    const optional = productAttributes.filter(pa => pa.optional && !pa.per_module && !pa.read_only && !pa.part_of_geometry);
    const perModule = productAttributes.filter(pa => pa.per_module && !pa.read_only && !pa.part_of_geometry);

    const data = {
        requiredAttributes: required,
        optionalAttributes: optional,
        perModuleAttributes: perModule,
        shapedAttributes: shaped
    };

    dispatch(productAttributesSet(data));
}

export const setRequiredSelections = (selections) => dispatch => {
    dispatch(productSelectionsLoading())
    dispatch(requiredSelectionsSet(selections))
}

export const setOptionalSelections = (selections) => dispatch => {
    dispatch(productSelectionsLoading())
    dispatch(optionalSelectionsSet(selections))
}

export const setShapedSelections = (selections) => dispatch => {
    dispatch(productSelectionsLoading())
    dispatch(shapedSelectionsSet(selections))
}

export const setPerModuleSelections = (selections) => dispatch => {
    dispatch(productSelectionsLoading())
    dispatch(perModuleSelectionsSet(selections))
}

/**
 * @description Handles changes to required and optional selections. Updates the store
 * to match values of current selections, if a required attribute is set for the first
 * time, this will also update the next attribute to have a blank selection thus displaying
 * on the page. After selections are updated, they are combined and sent to the productData
 * reducer.
 * @param field {object} The field being updated
 * @param selection {string} The new selection
 */
export const productSelectionChanged = (field, selection) => (dispatch, getState) => {
    dispatch(productSelectionsLoading());
    dispatch(setErrors([]));
    dispatch(setErrorFields([]));
    const state = getState();
    const existingSelections = getExistingSelections(field, state.productSelectionsReducer);

    const updatedSelections = [];
    let shouldSetNext = false;
    // we assume here that a matching product selection is already set
    // we do an if to avoid any errors that may occur.
    existingSelections.forEach((ps) => {
        let productSelection = ps;

        if(ps.id == field.id){
            if(ps.selection == ""){
                shouldSetNext = true;
            }
            productSelection = Object.assign({}, ps, {
                selection: selection
            })
        }
        updatedSelections.push(productSelection);
    });

    if(field.part_of_geometry) {
        dispatch(shapedSelectionsSet(updatedSelections))
    } else if(field.per_module){
        dispatch(perModuleSelectionsSet(updatedSelections))
    } else if(field.optional){
        dispatch(optionalSelectionsSet(updatedSelections))
    } else {
        dispatch(requiredSelectionsSet(updatedSelections))
        if(shouldSetNext) {
            dispatch(setNextSelection())
        }
    }

    if(field.part_of_geometry || field.internal_name === "shape_layout") {
        dispatch(setShapeElementsMatchSelections(false));
    }
    dispatch(setAttributeSelectionsFromSelections());
    if(getState().configuratorStateReducer.hasElements) {
        dispatch(elementParentUpdatedSelection(field, selection))
    }

    dispatch(manageConfiguratorState())
}

/**
 * @description Gets all fields of the same type (required, optional, module) and returns
 * them. Optional attributes will include empty values, the rest will not.
 * @param field {object} A field being update to get selections for
 * @param selectionsReducer {object} The current selections reducer
 * @returns {[]} All selections, optional include empty selections
 */
export const getExistingSelections = (field, selectionsReducer) => {
    let existingSelections = [];
    if(field.part_of_geometry){
        existingSelections = selectionsReducer.shapedSelections;
    } else if(field.per_module){
        existingSelections = selectionsReducer.perModuleSelections;
    } else if(field.optional){
        const optionalAttributes = selectionsReducer.optionalAttributes;
        optionalAttributes.forEach(oa => {
            const matchingSelection = selectionsReducer.optionalSelections.find(os => oa.id == os.id);
            existingSelections.push((matchingSelection ? matchingSelection : oa));
        })
    } else {
        existingSelections = selectionsReducer.requiredSelections;
    }
    return existingSelections;
}

// Sets the next selection for step by step effect
export const setNextSelection = () => (dispatch, getState) => {
    dispatch(productSelectionsLoading());
    const state = getState();
    const selections = Object.assign([], state.productSelectionsReducer.requiredSelections);
    const currentSelection = selections[selections.length - 1];
    const defaultSkuOrder = state.productSelectionsReducer.requiredAttributes[0].sku_order;
    const nextFieldSku = currentSelection ? currentSelection.sku_order + 1 : defaultSkuOrder;
    const selectionExists = selections.find(sel => sel.sku_order == nextFieldSku)

    // if it already exists we dont need to set it
    if(selectionExists) {
        dispatch(requiredSelectionsSet(selections))
    }

    const productAttributes = state.productSelectionsReducer.requiredAttributes;
    const productAttribute = productAttributes.find(pa => pa.sku_order == nextFieldSku );
    const isPreselected = productAttribute ? productAttribute.preselected : false;
    if(productAttribute) {
        if(isPreselected){
            selections.push(productAttribute);
        } else {
            const selection = Object.assign({}, productAttribute, {
                selection: ""
            });
            selections.push(selection);
        }

    }
    dispatch(requiredSelectionsSet(selections));
    if(isPreselected){
        // if it is preselected there wont be a selection to setup the next attribute
        // so we need to call it again.
        dispatch(setNextSelection());
    }
    dispatch(updateDisabledFields());
}

// Sets blank optional attribute values in selections for options that cant be found
export const setOptionalAttributes = () => (dispatch, getState) => {
    dispatch(productSelectionsLoading());
    const state = getState();
    const optionalSelections = []
    const optionalAttributes = state.productSelectionsReducer.optionalAttributes;
    // check attribute selections for optionalAttributes, if they cannot be found add them to the selections
    if(optionalAttributes){
        optionalAttributes.forEach(oa => {
            const matching = state.productSelectionsReducer.optionalSelections.find(os => os.id == oa.id);
            if(!matching){
                const optionalWithSelection = Object.assign({}, oa, {
                    selection: ""
                })
                optionalSelections.push(optionalWithSelection)
            } else {
                optionalSelections.push(matching);
            }
        })
    }
    dispatch(optionalSelectionsSet(optionalSelections));
    dispatch(setAttributeSelectionsFromSelections())

}

export const setShapedAttributes = () => (dispatch, getState) => {
    dispatch(productSelectionsLoading());
    const state = getState();
    const shapedSelections = []
    const shapedAttributes = state.productSelectionsReducer.shapedAttributes;
    if(shapedAttributes) {
        shapedAttributes.forEach( sa => {
            const matching = state.productSelectionsReducer.shapedSelections.find(ss => ss.id == sa.id);
            if(!matching){
                const optionalWithSelection = Object.assign({}, sa, {
                    selection: ""
                })
                shapedSelections.push(optionalWithSelection)
            } else {
                shapedSelections.push(matching);
            }
        })
    }
    dispatch(shapedSelectionsSet(shapedSelections));
    dispatch(setAttributeSelectionsFromSelections())


}

// Sets blank optional attribute values in selections
export const clearOptionalAttributes = () => (dispatch, getState) => {
    dispatch(productSelectionsLoading());
    const state = getState();
    const optionalSelections = []
    const optionalAttributes = state.productSelectionsReducer.optionalAttributes;
    // check attribute selections for optionalAttributes, if they cannot be found add them to the selections
    if(optionalAttributes){
        optionalAttributes.forEach(oa => {
            const optionalWithSelection = Object.assign({}, oa, {
                selection: ""
            })
            optionalSelections.push(optionalWithSelection)
        })
    }
    dispatch(optionalSelectionsSet(optionalSelections));

}

// Split single per_module attributes into individual ones per module.
// We will later combine them when passing them back to attribute_selections.
export const setEmptyPerModuleSelections = (modules) => (dispatch, getState) => {
    dispatch(productSelectionsLoading());
    const moduleSelections = [];
    modules.forEach(module => {
        if(!module.selection){
            const mod = Object.assign({}, module, {
                selections: []
            })
            moduleSelections.push(mod);
        }else {
            moduleSelections.push(module);
        }
    });
    dispatch(setPerModuleSelections(moduleSelections));

}


export const clearPerModuleSelections = () => (dispatch, getState) => {
    dispatch(productSelectionsLoading());
    const modules = getState().productSelectionsReducer.perModuleSelections;
    const moduleSelections = [];
    modules.forEach(module => {
        const mod = Object.assign({}, module, {
            selections: []
        })
        moduleSelections.push(mod);
    });
    dispatch(setPerModuleSelections(moduleSelections));

}

export const perModuleSelectionChanged = (module_id, value, module_key, attribute_data) => (dispatch, getState) => {
    dispatch(productSelectionsLoading())

    const state = getState();
    const currentSelections = state.productSelectionsReducer.perModuleSelections;
    const updatedSelections = getUpdatedModuleSelectionArray(currentSelections, module_id, module_key, value);

    dispatch(setPerModuleSelections(updatedSelections));
    if(attribute_data.pattern_linked_attribute_id){
        // dispatch(toggleLinkedAttributes(attribute_data.pattern_linked_attribute_id));
    }

    dispatch(setAttributeSelectionsFromSelections())
    dispatch(updateDisabledFields());
    // here we generate catalog number and refire the summary api call
    // since we know summary will be displaying if a module changes, we call
    // get summary directly instead of through the configuratorStateReducer.
    dispatch(generateCatalogNumber())
    dispatch(getSummary())
}

export const getUpdatedModuleSelectionArray = (currentSelections, module_id, module_key, value) => {
    let updatedSelections = [];
    currentSelections.forEach(selection => {
        let sel = selection;
        let selections = Object.assign([], selection.selections);
        if(selections.includes(module_key)){
            // containing module_key means it was previously selected, so deselect
            selections = selections.filter(sels => sels != module_key);
        } else {
            // otherwise add to selection
            selections.push(value);
        }
        selections = selections.filter(sel => sel);
        if(sel.module_id == module_id){
            sel = Object.assign({}, selection, {
                selections: selections
            })
        }
        updatedSelections.push(sel);
    })
    return updatedSelections
}

export const toggleLinkedAttributes = (attributeId) => (dispatch, getState) => {
    const state = getState();
    const productSelections = state.productDataReducer.attribute_selections;
    let matchingSelection = productSelections.find(ps => ps.id == attributeId);
        // we only update matching ids
    if(!matchingSelection) {
        // try to find product attribute that matches
        const attribute = state.productDataReducer.product_attributes.find(pa => pa.id == attributeId);
        if(attribute){
            matchingSelection = attribute;
        } else {
            return
        }
    }

    // get all per_module/optional/required
    const selectionsOfMatchingType = dispatch(getSelectionsOfType(matchingSelection));

    // modules have different structure, so handle them separately
    if(matchingSelection.per_module){
        const modules = [];
        const values = matchingSelection.values.split(",");
        selectionsOfMatchingType.forEach(module => {
            const current = module.selections;
            let updatedModule;
            if(current.includes(values[0])){
                const updatedSels = current.filter(sels => sels == values[0])
                updatedModule = Object.assign({}, module,{selections: updatedSels})
            } else {
                const updatedSels = Object.assign([], current);
                updatedSels.push(values[0]);
                updatedModule = Object.assign({}, module,{selections: updatedSels})
            }
            modules.push(updatedModule);
        })

        dispatch(setPerModuleSelections(modules));
    } else if(matchingSelection.optional) {
        const updatedSelections = [];
        selectionsOfMatchingType.forEach(sel => {
            if(sel.id == matchingSelection.id){
                let value = "";
                if(matchingSelection.selection == "" || !matchingSelection.selection){
                    value = matchingSelection.values.split(",")[0]
                }
                const updatedValue = Object.assign({}, matchingSelection, {
                    selection: value
                })
                updatedSelections.push(updatedValue);
            } else {
                updatedSelections.push(sel);
            }
        })

        dispatch(setOptionalSelections(updatedSelections));
    }
    dispatch(setAttributeSelectionsFromSelections())
}

export const getSelectionsOfType = (selection, state) => (dispatch, getState) => {
    // this method will return all per_module/optional/required attributes
    const state = getState();
    if(selection.per_module) {
        return state.productSelectionsReducer.perModuleSelections
    } else if(selection.optional && !selection.per_module){
        return state.productSelectionsReducer.optionalSelections
    } else {
        return state.productSelectionsReducer.requiredSelections
    }
}

// For part number lookup, splits returned attribute selections into the store
export const setSelectionsFromPartNumber = (apiResult, passedSelections ) => dispatch => {
    // split attribute_selections into corresponding selections
    let selections;
    const apiSelections = apiResult.product.attribute_selections;
    if(apiSelections.length === 0 || (passedSelections && apiSelections.length < passedSelections.length)) {
        selections = passedSelections
    } else {
        selections = apiSelections
    }
    const modules = apiResult.quote_item_modules;
    const shaped = selections.filter( pa => pa.part_of_geometry)
    const required = selections.filter(pa => !pa.optional && !pa.per_module && !pa.part_of_geometry);
    const optional = selections.filter(pa => pa.optional && !pa.per_module && !pa.part_of_geometry);
    const perModule = [];
    let moduleId = 1;
    if(modules) {
        modules.forEach(mod => {
            const moduleOptionWithSelection = mod.quote_item_module_options.find(qimo => qimo.selection == 1)
            let selection = [];
            if (moduleOptionWithSelection) {
                selection.push(moduleOptionWithSelection.module_catalog_key);
            }
            const moduleWithSelection = Object.assign({}, mod, {
                selections: selection,
                module_id: moduleId
            })
            perModule.push(moduleWithSelection);

            moduleId++
        })
    }
    dispatch(setRequiredSelections(required))
    dispatch(setOptionalSelections(optional))
    dispatch(setPerModuleSelections(perModule))
    dispatch(setShapedSelections(shaped))
    dispatch(setDisabledModules(perModule));
}

// sets attribute selections inside of productDataReducer
export const setAttributeSelectionsFromSelections = () => (dispatch, getState) => {
    const reducer = getState().productSelectionsReducer;

    const required = reducer.requiredSelections;
    const optional = reducer.optionalSelections.filter(opt => opt.selection);
    const perModule = reducer.perModuleSelections;
    const shaped = reducer.shapedSelections.filter(ss => ss.selection);

    const perModuleAttributes = reducer.perModuleAttributes;
    const mergedPerModuleSelections = convertModuleSelectionsToAttributeSelections(perModuleAttributes, perModule);

    // this is where we create quote_item_modules and their attached selections
    const modules = convertModuleDataToProductData(perModule, perModuleAttributes);

    const attributeSelections = required.concat(optional).concat(mergedPerModuleSelections).concat(shaped);
    dispatch(setQuoteItemModuleAttributeSelections(modules));
    dispatch(setAttributeSelections(attributeSelections));
    // dispatch(updateDisabledFields());
}
/**
 * @description Converts separated module selections into single selections e.g. two BP selections becomes B2
 * @param perModuleAttributes
 * @param perModuleSelections
 * @returns Attribute selections array {*[]}
 */
export const convertModuleSelectionsToAttributeSelections = (perModuleAttributes, perModuleSelections) => {
    const mergedPerModuleSelections = [];
    // here we go through module attributes and populate attribute selections with values
    perModuleAttributes.forEach(attribute => {
        let attributeCount = 0;
        perModuleSelections.forEach(selection => {
            const value = selection.selections.includes(attribute.module_catalog_key) ? 1:0;
            attributeCount += value;
        });

        const perModuleAsValue = Object.assign ({}, attribute, {
            selection: attributeCount != 0 ? attribute.module_catalog_key + attributeCount : ""
        })

        mergedPerModuleSelections.push(perModuleAsValue);
    })
    return mergedPerModuleSelections;
}

/**
 * @description Takes per module selections and normalizes them for the save and summary apis
 * @param moduleSelections
 * @param moduleAttributes
 * @returns {*[]}
 */
export const convertModuleDataToProductData = (moduleSelections, moduleAttributes) => {
    const data = []
    moduleSelections.forEach(mod => {
        const module = {
            length: mod.length,
            position: mod.position,
            order: mod.module_id,
            quote_item_module_attribute_selections_attributes: []
        };

        moduleAttributes.forEach(pma => {
            if(mod.selections.includes(pma.module_catalog_key)){
                module.quote_item_module_attribute_selections_attributes.push({
                    product_attribute_id: pma.id,
                    selection: pma.module_catalog_key
                })
            }
        })

        data.push(module);
    })

    return data;
}

/**
 * Check for empty selections inside a passed array of selections
 * @param selections - current selections
 * @returns {boolean} - true if any empty required attributes
 */
export const checkForEmptyRequiredSelection = (selections) => {
    const emptySelections = selections.filter(sel => !sel.optional && !sel.per_module && !sel.selection);
    return emptySelections.length != 0;
}

/**
 * Take shape values returned from summary and create matching elements and selections.
 * After creating update store values so they display on the front end.
 * Currently HARDCODING family - consider making it dynamic later
 * @param shapes
 */
export const displayGeneratedShapeElements = (shapes) => async (dispatch, getState) => {
    const FAMILY = 'LN1';
    const elements = getState().productDataReducer.product_elements;
    const CONNECTOR_CATALOGS = ['LN1E', 'LN1J', 'LN1V'];
    dispatch(mapSectionsToSide(shapes));
    // clear any existing elements as summary request provides them
    dispatch(clearElements());
    if(shapes.length == 0) {
        return;
    }

    for(let shape of shapes) {
        let familyCode = (FAMILY + shape['ELEM_CAT']).substring(0,4);
        let wasStripped = false
        if(CONNECTOR_CATALOGS.includes(familyCode)) {
            familyCode = 'LN1';
            wasStripped = true
        }
        const element = elements.find(el => el.family == familyCode);
        if(!element) {
            return;
        }
        // joiners catalog contains the first selection eg EA, so pass whole catalog instead of just the last digit
        const firstSelection = wasStripped ? shape['ELEM_CAT'] : shape['ELEM_CAT'][1];
        const secondSelection = shape['ELEM_PWR'];
        // remove nulls
        const toPass = [firstSelection, secondSelection].filter(v => v);
        await(dispatch(addElement(element, shape['ELEM_SECT'], toPass)));
    }

    dispatch(setShapeElementsMatchSelections(true))
    dispatch(manageConfiguratorState())
    dispatch(setShapeRequestPending(false))
}

const reducer = productSelectionsSlice.reducer;
export default reducer
